import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Modal, Alert, Spinner, Input, Button } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate } from '../constants/utils';
import PermissionGuard from '../components/Common/PermissionGuard';

const OrganizationApprovals = () => {
    document.title = "Organization Approvals | Arcmedix";
    const [statusFilter, setStatusFilter] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [apiError, setApiError] = useState('');
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Approve modal state
    const [approveModal, setApproveModal] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [seatLimit, setSeatLimit] = useState('');
    const [approveLoading, setApproveLoading] = useState(false);

    // Reject modal state
    const [rejectModal, setRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectLoading, setRejectLoading] = useState(false);

    const statusOptions = ['PENDING', 'APPROVED', 'REJECTED'];

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('count', count);
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('status', statusFilter);

            const { data } = await api.get(`organization-approval/pending?${params.toString()}`);
            setOrganizations(data.response || []);
            setHasMore((data.response || []).length >= count);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        if (!hasMore) return;
        setLoading(true);
        try {
            const newCount = count + 10;
            const params = new URLSearchParams();
            params.append('count', newCount);
            if (searchTerm) params.append('search', searchTerm);
            if (statusFilter) params.append('status', statusFilter);

            const { data } = await api.get(`organization-approval/pending?${params.toString()}`);
            setOrganizations(data.response || []);
            setCount(newCount);
            setHasMore((data.response || []).length >= newCount);
        } catch (error) {
            console.error('Error loading more:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        const parsedSeatLimit = parseInt(seatLimit, 10);
        if (!selectedOrg || isNaN(parsedSeatLimit) || parsedSeatLimit < 1) {
            setApiError('Seat limit must be a positive whole number');
            return;
        }

        setApproveLoading(true);
        setApiError('');
        try {
            await api.patch(
                `organization-approval/approve?organizationId=${selectedOrg.id}&seatLimit=${parsedSeatLimit}`
            );
            setApproveModal(false);
            setSelectedOrg(null);
            setSeatLimit('');
            fetchOrganizations();
        } catch (error) {
            setApiError(error?.response?.data?.error || 'Failed to approve organization');
        } finally {
            setApproveLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedOrg) return;

        setRejectLoading(true);
        setApiError('');
        try {
            await api.patch(
                `organization-approval/reject?organizationId=${selectedOrg.id}`,
                rejectReason.trim() ? { reason: rejectReason.trim() } : {}
            );
            setRejectModal(false);
            setSelectedOrg(null);
            setRejectReason('');
            fetchOrganizations();
        } catch (error) {
            setApiError(error?.response?.data?.error || 'Failed to reject organization');
        } finally {
            setRejectLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCount(10);
        setHasMore(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'APPROVED': return 'bg-success';
            case 'REJECTED': return 'bg-danger';
            case 'PENDING': return 'bg-warning text-dark';
            default: return 'bg-secondary';
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, [statusFilter, searchTerm]);

    return (
        <div className='page-content mt-4'>
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3 w-100"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder="Search by organization name, admin name, or email..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">Organization Approvals</h3>
                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-md w-auto"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                {statusOptions.map((stat) => (
                                    <option key={stat} value={stat}>
                                        {stat.charAt(0) + stat.slice(1).toLowerCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </CardHeader>

                <CardBody>
                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 1,
                            }}>
                                <tr>
                                    <th>#</th>
                                    <th>Organization</th>
                                    <th>Email</th>
                                    <th>Address</th>
                                    <th>Contact</th>
                                    <th>Admin</th>
                                    <th>Admin Email</th>
                                    <th>Status</th>
                                    <th>Seats</th>
                                    <th>Signed Up</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {organizations.map((org, ind) => (
                                        <motion.tr
                                            key={org?.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row">{ind + 1}</th>
                                            <td className="fw-semibold">{org?.name}</td>
                                            <td>{org?.email || '-'}</td>
                                            <td>{org?.address || '-'}</td>
                                            <td>{org?.contactNumber || '-'}</td>
                                            <td>{org?.adminUser?.name || '-'}</td>
                                            <td>{org?.adminUser?.email || '-'}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(org?.approvalStatus)}`}>
                                                    {org?.approvalStatus || 'Unknown'}
                                                </span>
                                            </td>
                                            <td>
                                                {org?.seatUsage ? (
                                                    <span>
                                                        {org.seatUsage.used} / {org.seatUsage.limit != null ? org.seatUsage.limit : 'Unlimited'}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            <td>{org?.registrationDate ? formatCreatedDate(org.registrationDate) : '-'}</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    {org?.approvalStatus === 'PENDING' && (
                                                        <PermissionGuard permission="organization:approve">
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedOrg(org);
                                                                    setSeatLimit('');
                                                                    setApiError('');
                                                                    setApproveModal(true);
                                                                }}
                                                                title="Approve"
                                                            >
                                                                <FeatherIcon icon="check" size={16} />
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedOrg(org);
                                                                    setRejectReason('');
                                                                    setApiError('');
                                                                    setRejectModal(true);
                                                                }}
                                                                title="Reject"
                                                            >
                                                                <FeatherIcon icon="x" size={16} />
                                                            </Button>
                                                        </PermissionGuard>
                                                    )}
                                                    {org?.approvalStatus === 'APPROVED' && (
                                                        <span className="text-muted small">Approved</span>
                                                    )}
                                                    {org?.approvalStatus === 'REJECTED' && (
                                                        <span className="text-muted small">Rejected</span>
                                                    )}
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {loading && (
                        <div className="text-center py-3">
                            <Spinner color="primary" />
                        </div>
                    )}

                    <div className="text-center mt-3">
                        {hasMore ? (
                            <Button
                                color="primary"
                                onClick={loadMore}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </Button>
                        ) : (
                            organizations.length > 0 && (
                                <div className="text-muted">No more organizations to load</div>
                            )
                        )}
                    </div>

                    {organizations.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted">
                            {searchTerm
                                ? "No organizations match your search"
                                : "No organizations found"}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Approve Modal */}
            <Modal
                isOpen={approveModal}
                toggle={() => { setApproveModal(false); setApiError(''); }}
                centered
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title fw-semibold">Approve Organization</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setApproveModal(false)}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body pt-2">
                        <div className="mb-3">
                            <p className="text-muted mb-2">
                                Approve <strong>{selectedOrg?.name}</strong> and set a seat limit.
                            </p>
                        </div>

                        {selectedOrg?.adminUser && (
                            <div className="d-flex align-items-center mb-4 p-3 bg-light rounded">
                                <div className="avatar-sm bg-soft-primary rounded-circle d-flex align-items-center justify-content-center">
                                    <FeatherIcon icon="user" className="text-primary" size={20} />
                                </div>
                                <div className="ms-3">
                                    <h6 className="mb-0">{selectedOrg.adminUser.name}</h6>
                                    <p className="text-muted small mb-0">{selectedOrg.adminUser.email}</p>
                                </div>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Seat Limit <span className="text-danger">*</span></label>
                            <Input
                                type="number"
                                min="1"
                                value={seatLimit}
                                onChange={(e) => setSeatLimit(e.target.value)}
                                placeholder="Enter maximum number of seats"
                            />
                            <small className="text-muted">
                                Maximum number of users that can join this organization.
                            </small>
                        </div>

                        {apiError && (
                            <Alert color="danger" className="mt-3">
                                <FeatherIcon icon="alert-circle" className="me-2" size={16} />
                                {apiError}
                            </Alert>
                        )}
                    </div>
                    <div className="modal-footer border-0 pt-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => setApproveModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success rounded-pill px-4"
                            onClick={handleApprove}
                            disabled={approveLoading || !seatLimit}
                        >
                            {approveLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Approving...
                                </>
                            ) : (
                                <>
                                    <FeatherIcon icon="check" className="me-2" size={16} />
                                    Approve
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={rejectModal}
                toggle={() => { setRejectModal(false); setApiError(''); }}
                centered
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title fw-semibold">Reject Organization</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setRejectModal(false)}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body pt-2">
                        <div className="mb-3">
                            <p className="text-muted mb-2">
                                Reject <strong>{selectedOrg?.name}</strong>. Please provide a reason (optional).
                            </p>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-semibold">Reason</label>
                            <Input
                                type="textarea"
                                rows={4}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                            />
                        </div>

                        {apiError && (
                            <Alert color="danger" className="mt-3">
                                <FeatherIcon icon="alert-circle" className="me-2" size={16} />
                                {apiError}
                            </Alert>
                        )}
                    </div>
                    <div className="modal-footer border-0 pt-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => setRejectModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger rounded-pill px-4"
                            onClick={handleReject}
                            disabled={rejectLoading}
                        >
                            {rejectLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Rejecting...
                                </>
                            ) : (
                                <>
                                    <FeatherIcon icon="x" className="me-2" size={16} />
                                    Reject
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </Modal>
        </div>
    );
};

export default OrganizationApprovals;
